import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  fetchComunicacoes,
  fetchDashboardStats,
  fetchProcessos,
} from '../../api/client';
import { fetchRegua } from '@/api/regua';
import { classifyError } from '../../lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Card,
  CardContent,
} from '../../components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { CampoDeBusca } from '@/components/ui/campo-de-busca';
import { AjudaDaBusca } from '@/components/ui/smart-search';
import { useAuth } from '../../hooks/useAuth';
import { useDjenProgress } from '../../hooks/useDjenProgress';
import { useAtuacao } from '@/lib/delegacao/atuacao';
import {
  type DashboardState,
  dashboardAnimationStyles,
  formatFullDate,
  getGreeting,
  primeiroNome,
} from './helpers';
import { EmptyWithOabState, EmptyNoOabState, FailedState, IngestingState } from './empty-states';
import { DjenSyncButton, RecentComunicacoes } from './widgets';
import { PrazosDeHoje } from './PrazosDeHoje';
import { Regua } from './Regua';
import { SessoesAudienciasCard } from './SessoesAudienciasCard';
import { ConvitesPendentes } from '@/components/escritorio/ConvitesPendentes';

import { ContasEmQueAtuoCard } from './ContasEmQueAtuoCard';
import { DistribuicaoPendenteCard } from './DistribuicaoPendenteCard';
import { SecretarioAtivacaoBanner } from '@/pages/secretario/SecretarioAtivacaoBanner';

// ---------------------------------------------------------------------------
// Componente principal: DashboardPage
// ---------------------------------------------------------------------------
function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [manualSyncActive, setManualSyncActive] = useState(false);

  // Primeiro acesso: OAB cadastrada mas nunca sincronizou — pular queries
  // (processos, comunicações e stats serão zero, economiza 3 roundtrips)
  // Na conta do titular: ele tem OAB (a delegação exige) e a sincronização do DJEN
  // é dele — nada de primeiro acesso nem de acompanhar a busca por aqui.
  const atuacao = useAtuacao();
  const hasOab = atuacao ? true : (user?.oabs?.length ?? 0) > 0;
  const isFirstAccess = !atuacao && hasOab && !user?.djen_ultimo_sync;

  // Capturar no mount — sobrevive ao refreshUser() que seta djen_ultimo_sync
  const [wasFirstAccess] = useState(isFirstAccess);

  // Dados do dashboard — desabilitados no primeiro acesso
  const { data: dashStats, error: statsError } = useQuery({
    queryKey: [...queryKeys.dashboard.stats],
    queryFn: fetchDashboardStats,
    enabled: !isFirstAccess,
  });

  // O topo do painel e a régua (Bloco F de docs/PLANO_CONTAGEM_PRAZOS.md). Os vencimentos
  // mudam no servidor sem ação nesta aba (recálculo pelo outbox depois de um feriado novo,
  // intimação do DJEN): validade curta e releitura ao voltar o foco — com os 5 min e sem
  // foco do padrão, o painel aberto seguia mostrando o dia que deixou de contar.
  const { data: regua, isLoading: loadingRegua, error: reguaError } = useQuery({
    queryKey: [...queryKeys.dashboard.regua],
    queryFn: fetchRegua,
    enabled: !isFirstAccess,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const { data: processos, isLoading: loadingProcessos, error: processosError } = useQuery({
    queryKey: [...queryKeys.processos.all],
    queryFn: () => fetchProcessos(),
    enabled: !isFirstAccess,
  });

  const { data: comunicacoesData, isLoading: loadingComunicacoes, error: comunicacoesError } = useQuery({
    queryKey: [...queryKeys.comunicacoes.all, { limit: 5 }],
    queryFn: () => fetchComunicacoes({ limit: 5 }),
    enabled: !isFirstAccess,
  });

  const totalProcessos = processos?.length ?? 0;

  // Job ativo no backend — sobrevive a navegação (dashStats refetch no mount)
  const hasActiveJob = dashStats?.djen_ultimo_status === 'RUNNING' || dashStats?.djen_ultimo_status === 'PENDING';

  // SSE: ativar no primeiro acesso, empty state, sync manual, ou job ativo
  const djenProgress = useDjenProgress(
    !atuacao &&
      (isFirstAccess || (hasOab && totalProcessos === 0 && !loadingProcessos) || manualSyncActive || hasActiveJob),
  );
  const [justCompleted, setJustCompleted] = useState<{ comunicacoes: number; processos: number } | null>(null);
  const [prevDjenStatus, setPrevDjenStatus] = useState<string>();

  // Detectar transição para COMPLETED durante render (padrão React recomendado)
  // Tela cheia "Busca concluída!" apenas no primeiro acesso (onboarding).
  // Sync manual e reconexão SSE: toast discreto via DjeSyncWidget.
  if (djenProgress?.status === 'COMPLETED' && prevDjenStatus !== 'COMPLETED') {
    setPrevDjenStatus('COMPLETED');
    if (wasFirstAccess && !manualSyncActive) {
      setJustCompleted({
        comunicacoes: djenProgress.comunicacoes ?? 0,
        processos: djenProgress.processos ?? 0,
      });
    }
  }
  if (djenProgress?.status && djenProgress.status !== 'COMPLETED' && prevDjenStatus === 'COMPLETED') {
    setPrevDjenStatus(djenProgress.status);
  }

  // Desativar SSE manual quando job concluir
  useEffect(() => {
    if (!manualSyncActive) return;
    if (djenProgress?.status === 'COMPLETED' || djenProgress?.status === 'FAILED') {
      const timer = setTimeout(() => setManualSyncActive(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [manualSyncActive, djenProgress?.status]);

  // Side effects ao concluir ingestão (invalidar queries imediatamente)
  useEffect(() => {
    if (djenProgress?.status === 'COMPLETED') {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.processos.all] });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.comunicacoes.all] });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.dashboard.stats] });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.dashboard.regua] });
      refreshUser().catch(() => {});
    }
  }, [djenProgress?.status, queryClient, refreshUser]);

  // Recuperação: se preso em INGESTING sem dados do SSE, verificar periodicamente
  // se o sync já completou no backend (cobre falha de SSE, CORS, timeout, etc.)
  useEffect(() => {
    if (!isFirstAccess || djenProgress) return;
    const interval = setInterval(async () => {
      try {
        await refreshUser();
      } catch {
        // Será retentado no próximo intervalo
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [isFirstAccess, djenProgress, refreshUser]);

  // Limpar justCompleted após 4 segundos
  useEffect(() => {
    if (justCompleted) {
      const timer = setTimeout(() => setJustCompleted(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [justCompleted]);

  // Máquina de estados do dashboard — sem LOADING global.
  // Cada seção exibe seu próprio skeleton enquanto sua query carrega.
  const dashboardState = useMemo((): DashboardState => {
    const isIngesting = djenProgress?.status === 'RUNNING' || djenProgress?.status === 'PENDING';
    const isFailed = djenProgress?.status === 'FAILED';

    // Primeiro acesso: ir direto para INGESTING (queries desabilitadas)
    if (isFirstAccess) return 'INGESTING';

    // Ingestão ativa ou falha com zero processos
    if (isIngesting && totalProcessos === 0) return 'INGESTING';
    if (isFailed && totalProcessos === 0) return 'INGEST_FAILED';

    // Após queries carregarem: avaliar empty states
    if (!loadingProcessos && totalProcessos === 0 && hasOab) return 'EMPTY_WITH_OAB';
    if (!loadingProcessos && totalProcessos === 0 && !hasOab) return 'EMPTY_NO_OAB';

    return 'NORMAL';
  }, [totalProcessos, hasOab, djenProgress, loadingProcessos, isFirstAccess]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/app/processos?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Determinar modo da barra de progresso
  const ingestMode = djenProgress?.status === 'RUNNING' ? 'determinate' : 'indeterminate';

  // ---- Header ----
  const header = (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight mb-0.5">
            {getGreeting()}, {primeiroNome(user?.nome)}
          </h2>
          <p className="text-sm text-muted-foreground">
            {formatFullDate()}
          </p>
        </div>
        {dashboardState === 'NORMAL' && (
          <div className="flex w-full items-center gap-2 sm:w-auto">
            {/* A busca no DJEN é do titular (rota dele): o delegado só acompanha. */}
            {!atuacao && (
              <DjenSyncButton
                ultimoSync={dashStats?.djen_ultimo_sync ?? user?.djen_ultimo_sync ?? null}
                ultimoStatus={dashStats?.djen_ultimo_status ?? null}
                djenProgress={manualSyncActive || hasActiveJob ? djenProgress : null}
                onSyncTriggered={() => setManualSyncActive(true)}
              />
            )}
            <form onSubmit={handleSearch} className="flex flex-1 gap-2 sm:flex-none">
              {/* A mesma busca inteligente do acervo: Enter abre o acervo com ela. */}
              <CampoDeBusca
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Buscar no acervo: número, parte, classe…"
                className="flex-1 sm:w-80"
                ajuda={
                  <>
                    <AjudaDaBusca />
                    <p className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
                      Enter abre o Acervo com a busca.
                    </p>
                  </>
                }
              />
            </form>
          </div>
        )}
      </div>
      {!atuacao && (
        <div className="mt-4 space-y-4">
          <ConvitesPendentes />
          <DistribuicaoPendenteCard />
          <ContasEmQueAtuoCard />
        </div>
      )}
    </div>
  );

  // ---- Empty states ----
  if (dashboardState === 'INGEST_FAILED') {
    return (
      <div className="space-y-6">
        {header}
        <FailedState djenProgress={djenProgress} />
      </div>
    );
  }

  if (dashboardState === 'INGESTING') {
    return (
      <>
        <style>{dashboardAnimationStyles}</style>
        <div className="space-y-6">
          {header}
          <IngestingState user={user} djenProgress={djenProgress} mode={ingestMode} />
        </div>
      </>
    );
  }

  if (dashboardState === 'EMPTY_WITH_OAB') {
    return (
      <div className="space-y-6">
        {header}
        {!atuacao && <SecretarioAtivacaoBanner />}
        {atuacao ? (
          // A busca no DJEN é do titular (rota dele): o delegado só acompanha.
          <EmptyState
            icon={<FileText />}
            title="Nenhum processo nesta conta ainda"
            description={`Os processos de ${atuacao.titularNome} aparecem aqui assim que forem cadastrados.`}
          />
        ) : (
          <EmptyWithOabState />
        )}
      </div>
    );
  }

  if (dashboardState === 'EMPTY_NO_OAB') {
    return (
      <div className="space-y-6">
        {header}
        {!atuacao && <SecretarioAtivacaoBanner />}
        <EmptyNoOabState />
      </div>
    );
  }

  // ---- Transição: busca concluída ----
  if (justCompleted) {
    return (
      <>
        <style>{dashboardAnimationStyles}</style>
        <div className="space-y-6">
          {header}
          <Card className="animate-dash-fade-in">
            <CardContent className="flex flex-col items-center text-center py-8">
              <div className="h-16 w-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4 animate-dash-scale-in">
                <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold mb-1">Busca concluída!</h3>
              <p className="text-muted-foreground">
                Encontramos {justCompleted.comunicacoes} {justCompleted.comunicacoes === 1 ? 'intimação' : 'intimações'} em {justCompleted.processos} {justCompleted.processos === 1 ? 'processo' : 'processos'}
              </p>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // ---- Estado NORMAL (dashboard completo) ----
  return (
    <div className="space-y-6">
      {header}

      {!atuacao && <SecretarioAtivacaoBanner />}

      {(statsError || processosError || comunicacoesError || reguaError) && (
        <Alert variant="destructive">
          <AlertDescription>
            {classifyError(statsError || processosError || comunicacoesError || reguaError).message}
          </AlertDescription>
        </Alert>
      )}

      <PrazosDeHoje regua={regua} loading={loadingRegua} />
      <Regua regua={regua} loading={loadingRegua} />

      {/* Intimações e sessões/audiências dividem a largura. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="min-w-0">
          <RecentComunicacoes comunicacoes={comunicacoesData?.items} loading={loadingComunicacoes} />
        </div>
        <div className="min-w-0">
          <SessoesAudienciasCard />
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
